import { useContext, useEffect, useState } from 'react';
import { InfoCard, Progress } from '@backstage/core-components';
import { Accordion, AccordionSummary } from '@material-ui/core';
import { gql, useQuery } from '@apollo/client';
import { SystemContext, useAppId } from '../../contexts/SystemContext';
import ExpandMoreIcon from '@material-ui/icons/ExpandMore';
import { DataTable } from 'primereact/datatable';
import { Column } from 'primereact/column';
import { DevHubAlert, DevHubGrid } from '@devxdiscover/devhub-ui';

interface AuditEvent {
  eventType: string;
  eventSubject: string;
  identity: string;
  timestamp: string;
  details: JSON;
}

const GetAuditEventsQuery = gql`
  query AuditEvents($filter: DevHubAuditEventFilter!) {
    devhub {
      auditEvents(filter: $filter) {
        eventType
        eventSubject
        identity
        timestamp
        details
      }
    }
  }
`;

const replacer = (key: string, value: any) => {
  const excludedKeys = ['applicationId', 'supergraphService', '_logger'];
  if (excludedKeys.includes(key)) {
    return undefined;
  }
  return value;
};

/**
 * App History component
 * @constructor
 */
export const AppHistory = () => {
  const [rows, setRows] = useState<AuditEvent[]>([]);
  const { app, getPolicyAccess } = useContext(SystemContext);
  const [expanded, setExpanded] = useState<boolean>(false);

  useEffect(() => {
    if (localStorage.getItem('theme') === 'devhub-dark') {
      import('primereact/resources/themes/md-dark-indigo/theme.css');
    } else {
      import('primereact/resources/themes/md-light-indigo/theme.css');
    }
  }, []);

  const canViewHistory = (): boolean => {
    return getPolicyAccess('AccessAuditEventsPolicy');
  };

  const handleQueryCompletion = (result: {
    devhub: { auditEvents: AuditEvent[] };
  }) => {
    const processedRows = (result?.devhub?.auditEvents ?? [])
      .map((audit: AuditEvent, index: number) => ({
        ...audit,
        id: index,
        timestamp: new Date(audit.timestamp).toLocaleString('en-US', {
          year: 'numeric',
          month: '2-digit',
          day: '2-digit',
          hour: '2-digit',
          minute: '2-digit',
          hourCycle: 'h23',
        }),
      }))
      .sort(
        (
          a: { timestamp: string | number | Date },
          b: { timestamp: string | number | Date },
        ) => new Date(b.timestamp).getTime() - new Date(a.timestamp).getTime(),
      );

    setRows(processedRows);
  };

  const { loading, error } = useQuery(GetAuditEventsQuery, {
    variables: {
      filter: {
        applicationId: useAppId(),
      },
    },
    skip: !canViewHistory(),
    onCompleted: result => handleQueryCompletion(result),
  });

  const toggleExpanded = () => {
    setExpanded(!expanded);
  };

  const renderDetails = (rowData: AuditEvent) => {
    return (
      <div style={{ whiteSpace: 'normal', wordWrap: 'break-word' }}>
        {JSON.stringify(rowData.details, replacer, '\n').replace(
          /['"{}]+/g,
          '',
        )}
      </div>
    );
  };

  if (loading && canViewHistory()) {
    return <Progress />;
  } else if (error) {
    return (
      <DevHubAlert severity="error">Error loading audit events</DevHubAlert>
    );
  }

  return (
    <>
      {canViewHistory() && (
        <DevHubGrid item xs={12}>
          <InfoCard title="Historical Changes">
            <Accordion expanded={expanded} onChange={toggleExpanded}>
              <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                {loading ? (
                  <Progress />
                ) : (
                  <>
                    {!expanded ? (
                      <DevHubGrid item xs={12}>
                        <DevHubAlert severity="info" variant="outlined">
                          Last Updated: {app?.updatedAt?.split('T')[0]}. Click
                          to expand all history.
                        </DevHubAlert>
                      </DevHubGrid>
                    ) : null}
                  </>
                )}
                {expanded && (
                  <DevHubGrid container>
                    <DevHubGrid item xs={12}>
                      <DevHubAlert severity="info" variant="outlined">
                        Showing all historical events for {app?.name}.
                      </DevHubAlert>
                    </DevHubGrid>
                  </DevHubGrid>
                )}
              </AccordionSummary>
              <DevHubGrid item>
                <DataTable
                  value={rows}
                  paginator
                  rows={10}
                  rowsPerPageOptions={[10, 25, 50, 100]}
                  dataKey="id"
                  rowHover
                  style={{ lineHeight: '1' }}
                >
                  <Column
                    field="timestamp"
                    header="Timestamp"
                    style={{
                      width: '200px',
                      whiteSpace: 'normal',
                      wordWrap: 'break-word',
                    }}
                  />
                  <Column
                    field="identity"
                    header="Identity"
                    style={{
                      width: '150px',
                      whiteSpace: 'normal',
                      wordWrap: 'break-word',
                    }}
                  />
                  <Column
                    field="eventSubject"
                    header="Subject"
                    style={{
                      width: '150px',
                      whiteSpace: 'normal',
                      wordWrap: 'break-word',
                    }}
                  />
                  <Column
                    field="eventType"
                    header="Type of Change"
                    style={{
                      width: '150px',
                      whiteSpace: 'normal',
                      wordWrap: 'break-word',
                    }}
                  />
                  <Column
                    header="Change Details"
                    body={renderDetails}
                    style={{
                      width: '300px',
                      whiteSpace: 'normal',
                      wordWrap: 'break-word',
                    }}
                  />
                </DataTable>
              </DevHubGrid>
            </Accordion>
          </InfoCard>
        </DevHubGrid>
      )}
    </>
  );
};
